/*
 * Settings → Notifications, the Agents section's first rows: desktop alerts
 * on this browser (permission status and an enable button), the chime, and
 * the follow-up email when a signal is still unseen after the countdown
 * (one digest per case workflow run instead of one per step).
 * Rendered inside the section's `ListCard`.
 */
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ListRow } from "@/components/ui/list-card";
import { Switch } from "@/components/ui/switch";

import {
  requestDesktopAlerts,
  setAlertSound,
  setDesktopAlerts,
  useAlertPrefs
} from "../alertPrefs";

export const AGENT_SIGNAL_TYPE = "agent.signal";
export const ESCALATE_AFTER_MINUTES = 10;

const Row: FC<{
  id: string;
  label: string;
  hint: string;
  children: React.ReactNode;
}> = ({ id, label, hint, children }) => (
  <ListRow
    title={
      <Label htmlFor={id} className="text-base text-fg">
        {label}
      </Label>
    }
    description={<span className="text-sm text-fg-3">{hint}</span>}
    control={children}
  />
);

export const AgentAlertPreferences: FC<{
  email: boolean;
  emailDisabled?: boolean;
  onEmailChange: (on: boolean) => void;
}> = ({ email, emailDisabled, onEmailChange }) => {
  const prefs = useAlertPrefs();
  const desktopHint =
    prefs.permission === "unsupported"
      ? "This browser can't show desktop alerts."
      : prefs.permission === "denied"
      ? "Blocked in your browser's site settings for this page."
      : prefs.permission === "default"
      ? "Not turned on for this browser yet."
      : "When the tab is in the background and an agent finishes or needs you.";

  return (
    <>
      <Row
        id="pref-agent-desktop"
        label="Desktop Alerts When an Agent Needs You"
        hint={desktopHint}
      >
        {prefs.permission === "granted" ? (
          <Switch
            id="pref-agent-desktop"
            checked={prefs.desktop}
            onCheckedChange={setDesktopAlerts}
          />
        ) : prefs.permission === "default" ? (
          <Button
            id="pref-agent-desktop"
            size="sm"
            onClick={() => void requestDesktopAlerts()}
          >
            Turn On
          </Button>
        ) : (
          <span className="text-sm text-fg-3">
            {prefs.permission === "denied" ? "Blocked" : "Unavailable"}
          </span>
        )}
      </Row>
      <Row
        id="pref-agent-sound"
        label="Play a Sound"
        hint="A short chime with each desktop alert."
      >
        <Switch
          id="pref-agent-sound"
          checked={prefs.sound}
          onCheckedChange={setAlertSound}
        />
      </Row>
      <Row
        id="pref-agent-email"
        label="Email Me If I Haven't Looked"
        hint={`Sent ${ESCALATE_AFTER_MINUTES} minutes after an agent needs you or finishes, unless you've opened it. Snoozing pushes it back. A case workflow run sends one summary when all its steps are done.`}
      >
        <Switch
          id="pref-agent-email"
          checked={email}
          disabled={emailDisabled}
          onCheckedChange={onEmailChange}
        />
      </Row>
    </>
  );
};
